import { PERMISSIONS } from '#server/constants/constants';
import { UserAccessListing } from '@/components';
import { Loader, Spinner } from '@/components/Common';
import { SelectDropDown } from '@/components/Common/SelectDropDown/SelectDropDown';
import { Input } from '@/components/ui';
import { SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { CACHE_TIME } from '@/constants';
import { trpc } from '@/data-loaders';
import { useHandleError, useHandleSuccess, useSchoolList } from '@/hooks';
import { useAwmsStore } from '@/store';
import { Button } from '@curtin-dxp/curtin-ui';
import { CircleCheckBig, User } from 'lucide-react';
import { ChangeEvent, useEffect, useRef, useState } from 'react';

interface AddUserProps {
  userId: string | undefined;
  setUserId: (userId: string | undefined) => void;
}
export const AddUser = ({ userId, setUserId }: AddUserProps) => {
  const sendToastAddUserSuccess = useHandleSuccess({ title: 'Add user access' });
  const permissions = Object.values(PERMISSIONS);
  const permissionItems = permissions.map((p, i) => ({ id: i + 1, name: p })); // id starts with 1 since SelectDropdown doesn't work with 0
  const [enteredStaffId, setEnteredStaffId] = useState<string>('');
  const [selectedPermission, setSelectedPermission] = useState<string | undefined>(PERMISSIONS.READ);
  const [selectedSchool, setSelectedSchool] = useState<number | undefined>(undefined);
  const [validId, setValidId] = useState<boolean>(false);
  const [userIdForListing, setUserIdForListing] = useState<string | undefined>(undefined);
  const lastErrorRef = useRef<any>(null);
  const {
    data: staffName,
    refetch,
    isFetching: isValidateStaffFetching,
    error: validateStaffIdError,
  } = trpc.admin.validateStaffId.useQuery(
    { staffId: enteredStaffId },
    {
      cacheTime: CACHE_TIME,
      enabled: false,
      retry: false,
    },
  );

  const trpcUtils = trpc.useUtils();
  const handlePermissionChange = (id: number): void => {
    setSelectedPermission(permissionItems.find((p) => p.id === id)?.name || '');
  };

  const handleSchoolChange = (id: number): void => {
    setSelectedSchool(id);
  };

  const handleStaffIdChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setValidId(false);
    setEnteredStaffId(event.target.value);
    setUserIdForListing('');
  };

  const handleValidateStaffId = async () => {
    refetch();
    setUserIdForListing(enteredStaffId);
    setUserId(enteredStaffId);
  };

  const handleError = useHandleError();
  const activeYear = useAwmsStore((state) => state.activeYear);
  const { data: schoolList, isLoading: isSchoolListLoading, error: schoolListError } = useSchoolList(activeYear);

  const handleInvalidateAndRefetch = async () => {
    trpcUtils.admin.getUserAccessStats.invalidate();
    trpcUtils.admin.getUserAccessStats.refetch();
    trpcUtils.admin.getUserAccessDetails.invalidate();
    trpcUtils.admin.getUserAccessDetails.refetch();
  };

  useEffect(() => {
    if (userId) {
      setEnteredStaffId(userId);
      setUserIdForListing(userId);
    } else {
      setUserIdForListing(undefined);
      setEnteredStaffId('');
      setValidId(false);
      setSelectedPermission(PERMISSIONS.READ);
      setSelectedSchool(schoolList?.[0]?.id);
    }
  }, [userId, schoolList]);

  useEffect(() => {
    if (staffName) {
      setValidId(true);
    }
  }, [staffName]);

  useEffect(() => {
    setSelectedSchool(schoolList?.[0]?.id);
  }, [schoolList]);

  useEffect(() => {
    if (schoolListError) {
      console.error('Error fetching school list:', { schoolListError });
      handleError([schoolListError]);
    }
    if (validateStaffIdError && validateStaffIdError !== lastErrorRef.current) {
      handleError([validateStaffIdError]);
    }

    lastErrorRef.current = validateStaffIdError;
  }, [handleError, schoolListError, validateStaffIdError]);

  const { mutate: addUserAccess } = trpc.admin.addUserAccess.useMutation({
    onSuccess: async () => {
      handleInvalidateAndRefetch();
      setUserIdForListing(enteredStaffId);
      sendToastAddUserSuccess();
    },
    onError: (error) => {
      handleError([error]);
    },
  });

  if (isSchoolListLoading) {
    return <Loader />;
  }
  return (
    <>
      <SheetContent
        className="flex h-auto !max-w-[1250px] flex-col items-start justify-start overflow-y-scroll border-l-0 bg-white p-0"
        onInteractOutside={(event) => event.preventDefault()}
      >
        <SheetHeader>
          <SheetTitle className="h-100 flex flex-shrink-0 items-start justify-between self-stretch text-sh3 font-bold text-primary-black-90">
            <div className="flex w-[750px] items-center gap-10 self-stretch py-5 pl-8">User access</div>
          </SheetTitle>
          <SheetDescription></SheetDescription>
        </SheetHeader>
        <div className="sticky top-0 w-full bg-white">
          <div className="flex w-[500px] flex-col gap-8 pl-8">
            <div className="flex w-[350px] justify-between">
              <div className="relative">
                <Input
                  type="text"
                  value={enteredStaffId}
                  className="w-[160px] border-secondary-grey-04 pr-10 text-b2"
                  onChange={handleStaffIdChange}
                  placeholder="Enter Curtin ID"
                />
                {validId && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-600">✔️</span>}
              </div>
              <Button
                variant="outline-secondary"
                className="h-10 border-black bg-black text-white hover:bg-transparent hover:text-black focus:outline-none focus-visible:ring-1
                  focus-visible:ring-inset focus-visible:ring-white focus-visible:ring-offset-0"
                aria-label="Validate Curtin ID"
                onClick={handleValidateStaffId}
              >
                Validate ID
                <CircleCheckBig className="ml-[10px] h-6 w-6" />
              </Button>
            </div>
            {validId && (
              <div className="text-sh3">
                {staffName?.familyName}, {staffName?.firstGivenName}
              </div>
            )}
            {isValidateStaffFetching && (
              <span className="flex items-center justify-start">
                <Spinner />
              </span>
            )}
            <SelectDropDown
              valueChangeCallback={handlePermissionChange}
              initialValue={permissionItems[0]?.id}
              list={permissionItems}
              className="w-[150px] border-secondary-grey-04 text-b2"
            ></SelectDropDown>
            <SelectDropDown
              valueChangeCallback={handleSchoolChange}
              initialValue={schoolList == null ? 0 : schoolList[0]?.id}
              list={schoolList}
              className="border-secondary-grey-04 text-b2"
            ></SelectDropDown>
          </div>
          <div className="mt-8 pl-8">
            <Button
              variant="outline-secondary"
              className="h-10 border-black bg-black text-white hover:bg-transparent hover:text-black focus:outline-none focus-visible:ring-1
                focus-visible:ring-inset focus-visible:ring-white focus-visible:ring-offset-0"
              aria-label="Admin View"
              onClick={() =>
                addUserAccess({
                  staffId: enteredStaffId,
                  schoolId: selectedSchool!,
                  permission: selectedPermission!,
                  year: activeYear,
                })
              }
            >
              Add user access
              <User className="ml-[10px] h-6 w-6" />
            </Button>
          </div>
        </div>
        {userIdForListing && <UserAccessListing userId={userIdForListing} />}
      </SheetContent>
    </>
  );
};
